import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import SmartToy from "@mui/icons-material/SmartToy";
import Person from "@mui/icons-material/Person";
import Close from "@mui/icons-material/Close";
import Send from "@mui/icons-material/Send";
import Summarize from "@mui/icons-material/Summarize";
import PriorityHigh from "@mui/icons-material/PriorityHigh";
import Shield from "@mui/icons-material/Shield";
import Rule from "@mui/icons-material/Rule";
import Insights from "@mui/icons-material/Insights";


import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { ActionIconItem } from '../others/ActionIconItem';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useApiResolver } from "../../hooks/useApiResolver";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";

export default function ChatBot({ data }) {
    const trigger = data.trigger;
    const { resolveValue } = useApiResolver();
    // image lightbox state
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [openImage, setOpenImage] = useState(false);

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const chatEndRef = useRef(null);
    const audioRef = useRef(new Audio("/sounds/airplane_ding.mp3"));

    const [anchorEl, setAnchorEl] = useState(null);
    const openQuickMenu = Boolean(anchorEl);

    const handleOpenQuickMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseQuickMenu = () => setAnchorEl(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const normalizeMarkdownLinks = (md) => {
        if (!md || typeof md !== "string") return md;
        return md
            .replace(/<url-([^>]+)>/g, "<$1>")
            .replace(/\[url-([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "[$1]($2)")
            .replace(/url-(https?:\/\/[^\s)]+)/g, "$1");
    };

    const iconMap = {
        Summarize: Summarize,
        PriorityHigh: PriorityHigh,
        Shield: Shield,
        Rule: Rule,
        Insights: Insights
    };

    const quickQuestions = data.chatBot?.quickQuestions?.map(q => ({
        text: q.quickQuestionPrompt,
        taskInstruction: q.taskInstruction,
        systemPrompt: q.systemPrompt,
        icon: React.createElement(iconMap[q.icon] || Summarize, { fontSize: "small", sx: { color: "primary.main" } })
    })) || [];

    const handleSend = async (message = input, task = data.chatBot.taskInstruction, prompt = data.chatBot.systemPrompt, isUserQuestion) => {
        try {
            let finalMessage = task?.trim();
            if (finalMessage) {
                if (isUserQuestion)
                    finalMessage = finalMessage.replace('{user_question}', message)?.trim();
            }
            else
                return;

            setMessages((prev) => [
                ...prev,
                { sender: "user", text: message }
            ]);

            if (message === input) {
                setInput("");
            }

            let bodyData = trigger[0]?.bodyData;
            const url = trigger[0]?.url;
            const method = trigger[0]?.method;

            bodyData.system_prompt = prompt;
            bodyData.task_instruction = finalMessage;
            bodyData.is_user_question = isUserQuestion;
            bodyData.path = "cache/ReportIris.json";
            bodyData.bucket = "ia-automation-storage-staging";
            bodyData.isFirstTime = true;

            console.log("bodyData ", bodyData);
            const res = await resolveValue({
                method,
                url,
                data: JSON.stringify(bodyData),
            });
            console.log("Respuesta ==> ", res);

            const obj = JSON.parse(res.data.output);
            const markdown_message = obj.markdown_message
            const agentMsg = normalizeMarkdownLinks(markdown_message);

            setMessages((prev) => [
                ...prev,
                { sender: "agent", markdown: agentMsg }
            ]);

            audioRef.current.play().catch(() => { });
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <>
            <ActionIconItem item={data} onClick={() => setIsOpen(!isOpen)} />

            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        width: isFullScreen ? 800 : 340,
                        height: isFullScreen ? 600 : 460,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        overflow: "hidden",
                        zIndex: 1300,
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <SmartToy fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                Agente IA
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="end">
                            <IconButton size="small" color="inherit" onClick={() => setIsFullScreen(!isFullScreen)}>
                                {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>
                            <IconButton size="small" color="inherit" onClick={() => setIsOpen(!isOpen)}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Chat Area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            backgroundColor: "#f4f6f8",
                            p: 2,
                        }}
                    >
                        {messages.map((msg, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        msg.sender === "user" ? "flex-end" : "flex-start",
                                    mb: 1.5,
                                }}
                            >
                                {msg.sender === "agent" && (
                                    <Avatar
                                        sx={{
                                            bgcolor: "primary.main",
                                            width: 28,
                                            height: 28,
                                            mr: 1,
                                        }}
                                    >
                                        <SmartToy fontSize="small" />
                                    </Avatar>
                                )}

                                <Box
                                    sx={{
                                        bgcolor:
                                            msg.sender === "user" ? "grey.300" : "primary.main",
                                        color:
                                            msg.sender === "user"
                                                ? "text.primary"
                                                : "primary.contrastText",
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 2,
                                        maxWidth: "70%",
                                        wordWrap: "break-word",
                                    }}
                                >
                                    {msg.markdown ? (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                p: ({ node, ...props }) => (
                                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "inherit" }} component="div" {...props} />
                                                ),
                                                // img: ({ src, alt }) => (
                                                //     <Box component="img" src={src} alt={alt} sx={{ maxWidth: "100%", mt: 1, borderRadius: 1 }} />
                                                // ),
                                                img: ({ src, alt }) => (
                                                    <Box
                                                        component="img"
                                                        src={src}
                                                        alt={alt}
                                                        sx={{ maxWidth: "100%", mt: 1, borderRadius: 1, cursor: "pointer" }}
                                                        onClick={() => {
                                                            setImageSrc(src);
                                                            setOpenImage(true);
                                                        }}
                                                    />
                                                ),
                                                // a: ({ href, children }) => (
                                                //     <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                                //         {children}
                                                //     </a>
                                                // ),
                                                a: ({ href, children }) => {
                                                    // fallback: if href is empty, try to extract url from link text
                                                    let link = href;
                                                    if (!link) {
                                                        const text = Array.isArray(children) ? children.map(c => (typeof c === "string" ? c : (c?.props?.children ?? ""))).join("") : (typeof children === "string" ? children : "");
                                                        const m = text.match(/(https?:\/\/[^\s)]+)/);
                                                        if (m) link = m[0];
                                                    }
                                                    return (
                                                        <a href={link || "#"} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                                            {children}
                                                        </a>
                                                    );
                                                },
                                            }}
                                        >
                                            {msg.markdown}
                                        </ReactMarkdown>
                                    ) : (
                                        <Typography variant="body2">{msg.text}</Typography>
                                    )}
                                </Box>

                                {msg.sender === "user" && (
                                    <Avatar
                                        sx={{
                                            bgcolor: "grey.500",
                                            width: 28,
                                            height: 28,
                                            ml: 1,
                                        }}
                                    >
                                        <Person fontSize="small" />
                                    </Avatar>
                                )}
                            </Box>
                        ))}
                        <div ref={chatEndRef} />
                    </Box>

                    <Divider />

                    <Box sx={{ display: "flex", alignItems: "center", p: 1.5 }}>
                        <IconButton
                            color="primary"
                            onClick={handleOpenQuickMenu}
                            sx={{ mr: 1 }}
                        >
                            <AutoFixHigh fontSize="small" />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={openQuickMenu}
                            onClose={handleCloseQuickMenu}
                            anchorOrigin={{ vertical: "top", horizontal: "left" }}
                            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                        >
                            {quickQuestions.map((q, idx) => (
                                <MenuItem
                                    key={idx}
                                    onClick={() => {
                                        handleCloseQuickMenu();
                                        handleSend(q.text, q.taskInstruction, q.systemPrompt, false);
                                    }}
                                    sx={{
                                        fontSize: "0.85rem",
                                        py: 0.75,
                                        gap: 1
                                    }}
                                >
                                    {q.icon}
                                    <Typography variant="body2" fontSize="0.85rem">
                                        {q.text}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>

                        <TextField
                            fullWidth
                            multiline
                            maxRows={3}
                            variant="outlined"
                            size="small"
                            placeholder="Escribe tu pregunta..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            sx={{ flex: 1, mr: 1 }}
                        />

                        <IconButton
                            color="primary"
                            onClick={() => handleSend()}
                            sx={{ bgcolor: "primary.light", color: "white" }}
                        >
                            <Send fontSize="small" />
                        </IconButton>
                    </Box>
                </Paper>
            </Slide>
        </>
    );
}
